import { useCallback, useEffect, useState } from "react";
import { Printer, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg, fmtTime, PAY_LABELS } from "@/lib/api";
import { directPrint } from "@/lib/qz";
import { courierDay } from "@/lib/receipts";
import { Stars } from "./RatingBox";
import { toast } from "sonner";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
const payOf = (k) => PAY_LABELS[k] || (k === "mercadopago" ? "Mercado Pago" : k);

function Stat({ label, value, strong, testId }) {
  return <div className="rounded-lg bg-muted/50 px-3 py-2"><p className="text-[11px] text-muted-foreground">{label}</p><p className={`font-mono ${strong ? "text-base font-bold" : "text-sm"}`} data-testid={testId}>{value}</p></div>;
}

function PrintSheet({ g, day, store }) {
  return (
    <div className="hidden print:block print-area p-2 font-mono text-xs">
      <p className="text-center font-bold">{store}</p>
      <p className="text-center font-bold">ENTREGAS DO DIA · {day.split("-").reverse().join("/")}</p>
      <p className="text-center">{g.name}</p>
      <p className="border-t border-dashed my-2" />
      {g.orders.map((o) => <div key={o.id} className="flex justify-between"><span>#{o.number} {o.customer}{o.status === "entrega" ? " (em rota)" : ""}</span><span>{brl(o.total)}</span></div>)}
      <p className="border-t border-dashed my-2" />
      <p>Entregas: {g.delivered} · Em rota: {g.on_route}</p>
      <p className="font-bold">Total entregue: {brl(g.total)}</p>
      <p>Taxas de entrega: {brl(g.fees)}</p>
      {Object.entries(g.by_payment).map(([k, v]) => <p key={k}>{payOf(k)}: {brl(v)}</p>)}
      <p className="font-bold">Dinheiro a prestar contas: {brl(g.cash)}</p>
      <p>Troco levado: {brl(g.change)}</p>
      <p className="mt-8 text-center">______________________________<br />Assinatura do entregador</p>
    </div>
  );
}

function CourierCard({ g, onPrint }) {
  const [open, setOpen] = useState(false);
  const key = g.courier_id || "none";
  return (
    <div className="card-surface p-4 space-y-3" data-testid={`courier-day-${key}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-semibold">{g.name} {g.phone && <span className="text-xs font-normal text-muted-foreground">· {g.phone}</span>}</p>
          {g.rating_avg ? <p className="flex items-center gap-1 text-xs text-muted-foreground"><Stars value={Math.round(g.rating_avg)} size="h-3.5 w-3.5" />{g.rating_avg} ({g.ratings} {g.ratings === 1 ? "avaliação" : "avaliações"})</p> : <p className="text-xs text-muted-foreground">Sem avaliações</p>}
        </div>
        <Button size="sm" variant="outline" onClick={() => onPrint(g)} data-testid={`courier-day-print-${key}`}><Printer className="h-4 w-4 mr-1" />Imprimir</Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Stat label="Entregas" value={g.delivered} testId={`courier-day-count-${key}`} />
        <Stat label="Em rota" value={g.on_route} />
        <Stat label="Total entregue" value={brl(g.total)} strong testId={`courier-day-total-${key}`} />
        <Stat label="Taxas de entrega" value={brl(g.fees)} />
        <Stat label="Dinheiro a prestar contas" value={brl(g.cash)} strong testId={`courier-day-cash-${key}`} />
        <Stat label="Troco levado" value={brl(g.change)} testId={`courier-day-change-${key}`} />
      </div>
      {Object.keys(g.by_payment).length > 0 && <p className="text-xs text-muted-foreground">{Object.entries(g.by_payment).map(([k, v]) => `${payOf(k)} ${brl(v)}`).join(" · ")}</p>}
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1 text-xs font-medium text-primary" data-testid={`courier-day-toggle-${key}`}><ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />{open ? "Ocultar pedidos" : `Ver ${g.orders.length} pedidos`}</button>
      {open && (
        <ul className="divide-y rounded-lg border text-xs">{g.orders.map((o) => (
          <li key={o.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
            <span className="font-mono">#{o.number}</span><span className="flex-1 min-w-[120px]">{o.customer} · {fmtTime(o.created_at)}{o.status === "entrega" && <span className="ml-1 text-amber-600">em rota</span>}</span>
            <span>{o.paid_online ? "Pago online" : payOf(o.payment_method)}{o.payment_method === "dinheiro" && !o.paid_online && o.change_for > o.total ? ` · troco ${brl(o.change_for - o.total)}` : ""}</span>
            {o.rating && <Stars value={o.rating.stars} size="h-3 w-3" />}
            <span className="font-mono font-semibold">{brl(o.total)}</span>
            {o.rating?.comment && <p className="basis-full text-muted-foreground italic">"{o.rating.comment}"</p>}
          </li>
        ))}</ul>
      )}
    </div>
  );
}

export function CourierDayDialog({ open, onOpenChange }) {
  const { session } = useAuth();
  const [day, setDay] = useState(today());
  const [data, setData] = useState(null);
  const [printing, setPrinting] = useState(null);
  const load = useCallback(() => api.get(`/delivery/courier-report?day=${day}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [day]);
  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { if (printing) { setTimeout(() => { window.print(); setPrinting(null); }, 100); } }, [printing]);
  const store = session.tenant.nome_fantasia;
  const print = async (g) => { if (!(await directPrint(session, "delivery", (pr) => courierDay(g, day, store, pr)))) setPrinting(g); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="courier-day-modal">
        <DialogHeader><DialogTitle>Entregas do dia por entregador</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-44" data-testid="courier-day-date" />
          <p className="text-xs text-muted-foreground">Entregas finalizadas e em rota do dia. "Dinheiro a prestar contas" soma os pedidos em dinheiro já entregues; "Troco levado" é o troco que o entregador saiu levando.</p>
        </div>
        <div className="space-y-3">
          {data?.couriers.map((g) => <CourierCard key={g.courier_id || "none"} g={g} onPrint={print} />)}
          {data && !data.couriers.length && <p className="py-8 text-center text-sm text-muted-foreground" data-testid="courier-day-empty">Nenhuma entrega neste dia.</p>}
        </div>
        {printing && <PrintSheet g={printing} day={day} store={store} />}
      </DialogContent>
    </Dialog>
  );
}
